import axios from 'axios';
import Auth from '../../utils/auth';
import { setCurrentUser } from '../../utils/currentUser';
import { getStore } from '../../../app/store';
import { newError } from '../errors';
import * as actionTypes from '../../constants/actions';
import { createCookie } from '../../utils/cookie';
import * as endpoints from '../../constants/endpoints';
import clientInfo from '../../../app/utils/clientInfo';
import platform, { canHandleCookie } from '../../utils/platform';
import { getUniqueDeviceId } from '../../../app/utils/device';
import fetchUserBySessionToken from '../../api/users/fetchUserBySessionToken';
import logoutApiCall from '../../api/users/logout';

const setLoggedCookie = () => {
  const state = getStore().getState();

  createCookie(
    'aula-logged',
    state.institution.general.institution,
    365,
    '',
    '.aula.education'
  );
};

export const loginStart = () => ({
  type: actionTypes.USER_LOGIN,
  status: 'started',
});

export const loginSuccess = (user, dispatch) => {
  dispatch({
    type: actionTypes.USER_LOGIN,
    status: 'success',
    user,
  });
};

export const loginError = (error, dispatch) => {
  dispatch(newError(error, 'login'));
  dispatch({
    type: actionTypes.USER_LOGIN,
    status: 'error',
    error,
  });
};

export const loginWithAuthCookie = (email) => (dispatch) =>
  new Promise(async (resolve) => {
    dispatch(loginStart());

    const domain = getStore().getState().institution.general.institution;
    let activeUser = null;
    if (!email) {
      activeUser = Auth.getActiveUserInDomain(domain);
    }

    const userEmail = email || activeUser;
    const account = Auth.getSession(domain, userEmail);

    if (!account) {
      loginError(new Error("Session doesn't exist"), dispatch);
      resolve({
        error: "Session doesn't exist",
      });
      return;
    }

    const sessionToken = account.session;
    try {
      const user = await fetchUserBySessionToken({
        sessionToken,
      });

      setCurrentUser(user);
      setLoggedCookie();
      loginSuccess(user, dispatch);
      Auth.setActiveDomain(domain, userEmail);
      resolve(user);
    } catch (fetchUserByTokenError) {
      const deviceId = await getUniqueDeviceId();

      if (canHandleCookie) {
        Auth.setLoggedOut(domain, userEmail);
      }

      await logoutApiCall({
        email: userEmail,
        sessionToken,
        deviceId,
      }).catch((logoutError) => {
        const errorMessage = `Error in login: (${fetchUserByTokenError.message}) and logout (${logoutError.message})`;
        const combinedError = new Error(errorMessage);
        loginError(combinedError, dispatch);

        resolve({
          error: errorMessage,
        });
      });

      loginError(fetchUserByTokenError, dispatch);

      resolve({
        error: fetchUserByTokenError.message,
      });
    }
  });

// Used in mobile login
export const loginWithCode = (code) => (dispatch) =>
  new Promise((resolve, reject) => {
    dispatch(loginStart());
    getUniqueDeviceId().then((deviceId) => {
      axios
        .post(endpoints.LOGIN_CODE_VALIDATE(), {
          code,
          deviceId,
          deviceData: {
            os: `${clientInfo['Operating System']} - ${clientInfo['Operating System Version']}`,
            platform,
          },
        })
        .then(
          async (result) => {
            try {
              const user = await fetchUserBySessionToken({
                sessionToken: result.data.token,
              });
              setCurrentUser(user);
              setLoggedCookie();
              loginSuccess(user, dispatch);
              resolve(user);
            } catch (error) {
              loginError(error, dispatch);
              reject();
            }
          },
          (loginValidationError) => {
            if (loginValidationError.response.status === 403) {
              loginError(
                {
                  status: loginValidationError.response.status,
                  message: 'Invalid code',
                },
                dispatch
              );
              resolve();
              return;
            }
            loginError(loginValidationError, dispatch);
            reject(loginValidationError);
          }
        );
    });
  });

const requestLoginStart = () => ({
  type: actionTypes.REQUEST_LOGIN,
  status: 'started',
});

const requestLoginSuccess = (dispatch) => {
  dispatch({
    type: actionTypes.REQUEST_LOGIN,
    status: 'success',
  });
};

const requestLoginError = (error, dispatch) => {
  dispatch(newError(error, 'login'));
  dispatch({
    type: actionTypes.REQUEST_LOGIN,
    status: 'error',
    error,
  });
};

export const requestLogin = (email) => (dispatch) =>
  new Promise((resolve, reject) => {
    dispatch(requestLoginStart());
    axios
      .post(endpoints.LOGIN_CODE_REQUEST(), {
        email,
      })
      .then(
        () => {
          requestLoginSuccess(dispatch);
          resolve();
        },
        (err) => {
          requestLoginError(err, dispatch);
          reject(err);
        }
      );
  });
